import { graphql } from 'gatsby';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import React, { useEffect, useMemo, useState } from 'react';
import SimpleReactLightbox from 'simple-react-lightbox'; // Used for Pinouts
import SwiperCore, { Navigation, Pagination } from 'swiper';
import Layout from '../components/Layout';
import { TabsHeader } from '../components/TabsHeader/TabsHeader';
import ProductHeader from '../components/product/ProductHeader';
import SEO from '../components/seo';

import Breadcrumbs from '../components/Breadcrumbs/Breadcrumbs';
import ConnectAndContribute from '../components/ConnectAndContribute/ConnectAndContribute';
import DownloadableResources from '../components/DownloadableResource/DownloadableResources';
import ProductCompatibility from '../components/product/ProductCompatibility';
import ProductFeatures from '../components/product/ProductFeatures';
import ProductSuggestedLibraries from '../components/product/ProductSuggestedLibraries';
import ProductTechSpec from '../components/product/ProductTechSpec';
import ProductTutorials from '../components/product/ProductTutorials';
import { Sidebar } from '../entities/Sidebar';
import { imageFor, isInside } from '../utils/functions';
import './product.scss';

import 'swiper/components/navigation/navigation.scss';
import 'swiper/components/pagination/pagination.scss';
import 'swiper/swiper-bundle.css';
import Feedback from '../components/Feedback/Feedback';
import TutorialSideBar from '../components/SideBar/TutorialSidebar';
import { BACK, INTERACTIVE, PRO, SIDEBAR_HARDWARE } from '../utils/constants';

/* This code will be useful when using the LibraryRecord */
import { useLocation } from '@reach/router';
import algoliasearch from 'algoliasearch/lite';
import LibraryRecord from '../components/LibraryRecord/LibraryRecord';
import PageSubtitle from '../components/PageSubtitle/PageSubtitle';
import ProductCertifications from '../components/product/ProductCertifications';
const searchClient = algoliasearch(process.env.GATSBY_ALGOLIA_APP_ID, process.env.GATSBY_ALGOLIA_INDEXER_KEY);

// inizialize SwiperCore
SwiperCore.use([Navigation, Pagination]);

interface ProductProps {
  pageContext: any;
  data: {
    families: {
      edges: Array<any>;
    };
    categories: {
      edges: Array<any>;
    };
    images: {
      edges: Array<any>;
    };
    products: {
      edges: Array<any>;
    };
    datasheet: {
      fields: any;
      childMdx: any;
      frontmatter: {
        identifier: string;
      };
    };
    productCertifications: {
      childMdx: {
        frontmatter: {
          productCertifications: Array<{
            id: number;
            name: string;
            title: string;
            description: string;
            source: string;
            sourceTitle: string;
            files: any;
          }>;
        };
      };
    };
    certificationFiles: any;
    product: {
      body: string;
      fields: any;
      childMdx: any;
      frontmatter: {
        name: string;
        url_shop: string;
        url_guide: string;
        hasSetupGuide: boolean;
        core: string;
        certifications: Array<any>;
        forumCategorySlug: string;
        status: string;
        primary_button_url?: string;
        primary_button_title?: string;
        secondary_button_url?: string;
        secondary_button_title?: string;
        essentials_title?: string; // Used to customize the title of "Suggested Libraries" on product pages Tab
        sku?: Array<string>;
      };
    };
    image: IGatsbyImageData | string;
    features: {
      childMdx: {
        body: string;
      };
    };
    techspecs: any;
    techSpecInfo: {
      childMdx: any;
    };
    revision: {
      childMdx: any;
    };
    compatibility: {
      fields: {
        content: string;
      };
    };
    downloads: any;
    essentials: {
      childMdx: {
        body: any;
        frontmatter: {
          productsLibrariesMap: Array<string>;
        };
      };
    };
    tutorials: any;
    interactive: any;
    troubleshooting: any;
    getInspired: any;
    suggestions: any;
    previousDocumentation: {
      edges: Array<any>;
    };
  };
}

const getProductCertifications = (certificationsFiles, certifications) => {
  const certificationList = [];

  if (!certificationsFiles.edges || !certifications) return certificationList;

  certificationsFiles.edges.forEach((certificationFile) => {
    const baseName = certificationFile.node.name.split('_')[2];
    const certification = certifications.find((cert) => cert.name.includes(baseName));
    if (certification) {
      certificationList.push({
        baseName: baseName,
        name: certificationFile.node.name.split('_')[3]
          ? certificationFile.node.name.split('_')[2] + ` (${certificationFile.node.name.split('_')[3]})`
          : certificationFile.node.name.split('_')[2],
        url: certificationFile.node.publicURL,
        description: certifications.filter((cert) => cert.name === baseName)[0].description,
        type: certificationFile.node.name.split('_')[1].slice(-4) === 'CERT' ? 'Certification' : 'Declarations of Conformity',
        sku: certificationFile.node.name.split('_')[1].split('-')[0],
        source: certifications.filter((cert) => cert.name === baseName)[0].source,
        sourceTitle: certifications.filter((cert) => cert.name === baseName)[0].sourceTitle,
      });
    }
  });
  return certificationList;
};

export default function Product({ data }: ProductProps): React.ReactElement {
  //const isDesktop = typeof window !== 'undefined' ? window.innerWidth > 768 : true;
  const [domLoaded, setDomLoaded] = useState(true);
  const location = useLocation();

  const certifications = getProductCertifications(data.certificationFiles, data.productCertifications?.childMdx.frontmatter.productCertifications);

  useEffect(() => {
    setDomLoaded(true);
  }, []);
  function getProductDataRefactored(data: any) {
    let refactoredData: Sidebar[] = [];

    data?.families.edges.forEach((family) => {
      const categories = data.categories.edges.filter((category) => isInside(category, family));
      const images = imageFor(data.images, family);

      const nestedCat = categories
        .sort((a, b) => (a.node.relativeDirectory < b.node.relativeDirectory ? -1 : 1))
        .map((category) => {
          const nestedChild = data.products.edges
            .filter((product) => isInside(product, category))
            .sort((a, b) => (a.node.relativeDirectory < b.node.relativeDirectory ? -1 : 1))
            .map((product) => {
              if (product.node.childMdx) {
                return {
                  title: product.node.childMdx.frontmatter.title,
                  url: product.node.fields.slug,
                };
              } else return null;
            });
          return { category: category.node.childMdx.frontmatter.title, nestedChildren: nestedChild };
        });
      if (family.node.childMdx) {
        refactoredData.push({
          firstLevelCategory: family.node.childMdx.frontmatter.title,
          image: images,
          nestedCategories: nestedCat,
        });
      }
    });

    return refactoredData;
  }

  const productRefactoredData = useMemo(() => {
    return getProductDataRefactored(data);
  }, [data]);

  const tabsData = useMemo(() => {
    return [
      {
        title: 'Features',
        url: 'features',
        children: data?.features?.childMdx ? <ProductFeatures features={data.features.childMdx} /> : null,
      },
      {
        title: 'Tutorials',
        url: 'tutorials',
        children:
          data.tutorials.edges.length > 0 ? <ProductTutorials tutorials={data.tutorials} product={data.product.childMdx.frontmatter.title} /> : null,
      },
      {
        title: 'Tech Specs',
        url: 'tech-specs',
        children: data.techSpecInfo?.childMdx ? (
          <ProductTechSpec
            info={data.techSpecInfo?.childMdx}
            techspecs={data.techspecs}
            certifications={data.product.childMdx.frontmatter?.certifications}
          />
        ) : null,
      },
      {
        title: 'Compatibility',
        url: 'compatibility',
        children: data.compatibility ? <ProductCompatibility compatibility={data.compatibility.fields.content} /> : null,
      },
      {
        title: data?.product?.childMdx?.frontmatter?.essentials_title || 'Suggested Libraries',
        url: 'suggested-libraries',
        children: (
          <SuggestedLibrariesFormatter
            productsLibrariesMap={data.essentials?.childMdx?.frontmatter?.productsLibrariesMap}
            essentials={data.essentials?.childMdx}
          />
        ),
      },
    ];
  }, [data]);

  const isProProduct = useMemo(() => {
    let found = false;
    data.families.edges.forEach((family) => {
      if (family.node.childMdx.frontmatter.businessUnit === PRO) {
        productRefactoredData.forEach((product) => {
          if (family.node.childMdx.frontmatter.title === product.firstLevelCategory) {
            product.nestedCategories.forEach((cat) => {
              cat.nestedChildren.forEach((category) => {
                if (category && category.title === data.product.childMdx.frontmatter.title) {
                  found = true;
                  return found;
                }
              });
            });
          }
        });
      }
    });
    return found;
  }, [data.families.edges, data.product.childMdx.frontmatter.title, productRefactoredData]);

  //find simple pinout image
  const interactiveBoardSimplePinout = data.interactive.edges.find((element) => element.node.base.endsWith(INTERACTIVE[2].file));

  return (
    <>
      {domLoaded && (
        <Layout>
          {data.product.childMdx?.frontmatter?.title && (
            <SEO
              title={data.product.childMdx.frontmatter.title}
              description={data.product.childMdx.excerpt}
              image={data.image as IGatsbyImageData}
              isProductSvg={true}
              simplePinoutImage={interactiveBoardSimplePinout}
            />
          )}
          <main className="page-container">
            <TutorialSideBar
              links={[]}
              nestedCategories={productRefactoredData}
              selectedItem={data.product.childMdx.frontmatter.title}
              actualPage={SIDEBAR_HARDWARE}
              prevPage={{ title: BACK, pathname: '/hardware/' }}
            />
            <div className="page-layout product">
              <SimpleReactLightbox>
                <Breadcrumbs
                  actualPage={data.product.childMdx.frontmatter.title}
                  list={[
                    { title: 'Home', url: '/' },
                    { title: 'Hardware', url: '/hardware/' },
                    { title: data.product.childMdx.frontmatter.title, url: data.product.fields.slug },
                  ]}
                />
                <div className="product__layout">
                  <ProductHeader
                    product={data.product.childMdx}
                    image={data.image as IGatsbyImageData}
                    interactive={data.interactive}
                    purchaseLink={data.product.childMdx.frontmatter?.url_shop}
                  />
                  <DownloadableResources
                    product={data.product.childMdx.frontmatter.title}
                    downloads={data.downloads}
                    identifier={data?.datasheet?.childMdx?.frontmatter?.identifier}
                  />
                  <TabsHeader tabs={tabsData} activeTab="Features" slidesPerView={3} />
                </div>
              </SimpleReactLightbox>
              <ProductCertifications productTitle={data.product.childMdx.frontmatter.title} certifications={certifications} />
              <ConnectAndContribute
                showContactButton={isProProduct}
                forumLink={data.product.childMdx.frontmatter.forumCategorySlug}
                projectHubLink={data.product.childMdx.frontmatter.title}
              />
              <Feedback
                params={{
                  type: 'product',
                  title: data.product.childMdx.frontmatter.title,
                  slug: data.product.fields.slug,
                  url: location.href,
                }}
              />
            </div>
          </main>
        </Layout>
      )}
    </>
  );
}

export const pageQuery = graphql`query ($directory: String!, $downloadsDirectory: String!, $certificationsDirectory: String!, $datasheetDirectory: String!, $previousDocumentationDirectory: String!, $interactiveDirectory: String!, $product: String!) {
  product: file(relativeDirectory: {eq: $directory}, base: {eq: "product.md"}) {
    fields {
      category
      slug
    }
    childMdx {
      body
      excerpt(pruneLength: 200)
      frontmatter {
        title
        url_shop
        url_guide
        hasSetupGuide
        core
        certifications
        forumCategorySlug
        status
        primary_button_url
        primary_button_title
        secondary_button_url
        secondary_button_title
        essentials_title
        sku
      }
    }
  }
  productCertifications: file(base: {eq: "productCertifications.md"}) {
    childMdx {
      frontmatter {
        productCertifications {
          id
          name
          title
          description
          source
          sourceTitle
          files {
            title
            url
          }
        }
      }
    }
  }
  datasheet: file(relativeDirectory: {eq: $datasheetDirectory}, base: {eq: "datasheet.md"}) {
    fields {
      slug
    }
    childMdx {
      body
      excerpt(pruneLength: 200)
      frontmatter {
        identifier
      }
    }
  }
  image: file(
    relativeDirectory: {eq: $directory}
    base: {regex: "/\\image.(jpe?g|png|gif|bmp|svg)$/i"}
  ) {
    childImageSharp {
      gatsbyImageData(layout: FIXED, width: 354, quality: 90, placeholder: BLURRED)
    }
    extension
    publicURL
  }
  features: file(relativeDirectory: {eq: $directory}, base: {eq: "features.md"}) {
    childMdx {
      body
    }
  }
  techSpecInfo: file(
    relativeDirectory: {eq: $directory}
    base: {eq: "tech-specs.md"}
  ) {
    childMdx {
      body
    }
  }
  techspecs: file(
    relativeDirectory: {eq: $directory}
    base: {eq: "tech-specs.yml"}
  ) {
    fields {
      content
    }
  }
  revision: file(relativeDirectory: {eq: $directory}, base: {eq: "revision.md"}) {
    childMdx {
      body
    }
  }
  compatibility: file(
    relativeDirectory: {eq: $directory}
    base: {eq: "compatibility.yml"}
  ) {
    fields {
      content
    }
  }
  essentials: file(
    relativeDirectory: {eq: $directory}
    base: {eq: "essentials.md"}
  ) {
    childMdx {
      frontmatter {
        productsLibrariesMap
      }
      body
    }
  }
  suggestions: file(
    relativeDirectory: {eq: $directory}
    base: {eq: "suggestions.md"}
  ) {
    childMdx {
      body
    }
  }
  downloads: allFile(filter: {relativeDirectory: {eq: $downloadsDirectory}}) {
    edges {
      node {
        base
        publicURL
      }
    }
  }
  previousDocumentation: allFile(
    filter: {relativeDirectory: {eq: $previousDocumentationDirectory}}
  ) {
    edges {
      node {
        base
        name
        publicURL
      }
    }
  }
  interactive: allFile(filter: {relativeDirectory: {eq: $interactiveDirectory}}) {
    edges {
      node {
        base
        publicURL
      }
    }
  }
  tutorials: allFile(
    filter: {fields: {product: {eq: $product}}}
    sort: {relativeDirectory: ASC}
  ) {
    edges {
      node {
        fields {
          slug
          gitLogLatestDate
        }
        childMdx {
          frontmatter {
            title
            description
            tags
            author
            difficulty
          }
        }
      }
    }
  }
  families: allFile(filter: { base: { eq: "family.md" } }, sort: { fields: { order: ASC } }) {
    edges {
      node {
        relativeDirectory
        childMdx {
          frontmatter {
            title
            description
            businessUnit
            anchor
          }
        }
      }
    }
  }
  categories: allFile(filter: { base: { eq: "category.md" } }) {
    edges {
      node {
        relativeDirectory
        childMdx {
          frontmatter {
            title
          }
        }
      }
    }
  }
  products: allFile(filter: { base: { eq: "product.md" } }) {
    edges {
      node {
        relativeDirectory
        fields {
          slug
          category
        }
        childMdx {
          frontmatter {
            title
            core
            certifications
          }
        }
      }
    }
  }
  images: allFile(filter: { base: { eq: "image.png" } }) {
    edges {
      node {
        relativeDirectory
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED)
        }
      }
    }
  }
  certificationFiles: allFile(filter: {relativeDirectory: {eq: $certificationsDirectory}}) {
    edges {
      node {
        publicURL
        name
        relativeDirectory
      }
    }
  }
}`;

const getLibraryHits = async (productsLibrariesMap: Array<string>) => {
  try {
    const index = searchClient.initIndex(process.env.GATSBY_ALGOLIA_INDEX_NAME);

    if (productsLibrariesMap.length === 0) {
      return [];
    }

    const objectIDFilter = `objectID:${productsLibrariesMap.map((id) => `"${id}"`).join(' OR objectID:')}`;
    const filters = `documentation_type:"Library" AND (${objectIDFilter})`;

    const result = await index.search('', {
      filters,
      hitsPerPage: 1000,
    });

    return result.hits;
  } catch (error) {
    return [];
  }
};

export function SuggestedLibrariesFormatter(props: any): React.ReactElement {
  const { productsLibrariesMap, essentials } = props;

  const [libraryHits, setLibraryHits] = useState([]);

  useEffect(() => {
    const fetchLibraryHits = async () => {
      const hits = await getLibraryHits(productsLibrariesMap || []);
      return hits;
    };
    const getHits = async () => {
      const hits = await fetchLibraryHits();
      setLibraryHits(hits);
    };

    getHits();
  }, [productsLibrariesMap, setLibraryHits]);

  return (
    <div>
      {productsLibrariesMap &&
        libraryHits.map((library) => (
          <LibraryRecord
            key={library.name + ' ' + library.latest}
            link={process.env.GATSBY_DOCS_URL + 'libraries/' + library.objectID}
            title={library.title}
            type={library.categories || []}
            description={library.description}
            author={library.author?.split(' ')[0] || ''}
            version={library.latest}
            maintainer={library.maintainer || ''}
            date={new Date(library.release_date).toLocaleString([], {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            })}
            stars={library.stars}
            forks={library.forks}
          />
        ))}
      <br />
      {essentials && (
        <>
          <PageSubtitle title="External Resources" />
          <ProductSuggestedLibraries essentials={essentials} openGettingStartedHandler={null}>
            {essentials.body}
          </ProductSuggestedLibraries>
        </>
      )}
    </div>
  );
}
